import { useEffect, useRef, useCallback, useState } from "react";

const extractVinnyFromReportHtml = (reportHtmlContent) => {
  if (typeof window === "undefined") {
    return {
      vinnyHtml: null,
      vinnyScripts: [],
      vinnyStylesheets: [],
      modifiedReportHtml: null,
    };
  }
  if (!reportHtmlContent || typeof reportHtmlContent !== "string") {
    return {
      vinnyHtml: null,
      vinnyScripts: [],
      vinnyStylesheets: [],
      modifiedReportHtml: null,
    };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(reportHtmlContent, "text/html");
    const targetElement = doc.querySelector("#vinny-container");

    if (targetElement) {
      const vinnyScripts = Array.from(
        targetElement.querySelectorAll("script")
      ).map((script) => {
        const scriptUrl =
          script.getAttribute("th:src") || script.getAttribute("src");
        return { content: script.textContent, url: scriptUrl || "" };
      });

      const vinnyStylesheets = Array.from(
        targetElement.querySelectorAll("link")
      ).map((link) => {
        const styleUrl =
          link.getAttribute("th:href") || link.getAttribute("href");
        return { url: styleUrl || "" };
      });

      // Remove the tags from the original doc to avoid double execution
      targetElement
        .querySelectorAll("script, link")
        .forEach((tag) => tag.remove());
      targetElement.remove();

      const vinnyHtml = targetElement.innerHTML;
      const modifiedReportHtml = doc.documentElement.outerHTML;
      return { vinnyHtml, vinnyScripts, vinnyStylesheets, modifiedReportHtml };
    } else {
      return {
        vinnyHtml: null,
        vinnyScripts: [],
        vinnyStylesheets: [],
        modifiedReportHtml: reportHtmlContent,
      };
    }
  } catch (error) {
    console.error("Error parsing report html to extract vinny prompt:", error);
    return {
      vinnyHtml: null,
      vinnyScripts: [],
      vinnyStylesheets: [],
      modifiedReportHtml: reportHtmlContent,
    };
  }
};

const useDynamicDependencies = (scripts, stylesheets, onAllScriptsLoaded) => {
  useEffect(() => {
    const loadedElements = [];
    const head = document.head || document.getElementsByTagName("head")[0];

    // Load stylesheets
    stylesheets.forEach((sheet) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = sheet.url;
      head.appendChild(link);
      loadedElements.push(link);
    });

    // Track external script sources count
    const externalScripts = scripts.filter((s) => s.url);
    let scriptsToLoad = externalScripts.length;

    // For each script load, configure handleScriptLoad and once all are loaded invoke callback onAllScriptsLoaded
    const handleScriptLoad = () => {
      scriptsToLoad--;
      if (scriptsToLoad === 0 && onAllScriptsLoaded) {
        onAllScriptsLoaded();
      }
    };

    // Load scripts
    scripts.forEach((script) => {
      const scriptElement = document.createElement("script");
      scriptElement.type = "text/javascript";
      scriptElement.async = true;

      if (script.url) {
        scriptElement.src = script.url;
        scriptElement.onload = handleScriptLoad;
        scriptElement.onerror = () => {
          console.error(`Error loading script from ${script.url}`);
          // Still call the handle to ensure callback invocation
          handleScriptLoad();
        };
      } else if (script.content) {
        scriptElement.appendChild(document.createTextNode(script.content));
      }

      head.appendChild(scriptElement);
      loadedElements.push(scriptElement);
    });

    return () => {
      loadedElements.forEach((el) => el.parentNode?.removeChild(el));
    };
  }, [scripts, stylesheets, onAllScriptsLoaded]);
};

const VinnyPrompt = ({ htmlString, scripts, stylesheets }) => {
  const containerRef = useRef(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [htmlRendered, setHtmlRendered] = useState(false);
  const hasInitialized = useRef(false);

  const handleScriptsLoaded = useCallback(() => {
    setScriptsLoaded(true);
  }, []);

  useDynamicDependencies(scripts, stylesheets, handleScriptsLoaded);

  useEffect(() => {
    if (containerRef.current) {
      setHtmlRendered(true);
    }
  }, [htmlString]);

  useEffect(() => {
    if (
      scriptsLoaded &&
      htmlRendered &&
      window.initVinny &&
      !hasInitialized.current
    ) {
      try {
        window.initVinny();
        hasInitialized.current = true;
      } catch (error) {
        console.error("Error initializing Vinny prompt:", error);
      }
    } else if (scriptsLoaded && htmlRendered && !window.initVinny) {
      console.warn(
        "Unable to initialize Vinny prompt due to missing init routine"
      );
    }
  }, [scriptsLoaded, htmlRendered]);

  return (
    <div
      ref={containerRef}
      dangerouslySetInnerHTML={{ __html: htmlString || "" }}
    />
  );
};

export { extractVinnyFromReportHtml, VinnyPrompt };
